import { useEffect, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass, Check, SealCheck } from "@phosphor-icons/react";
import { api } from "../lib/api";

function Avatar({ name, src }) {
  return (
    <span className="w-6 h-6 rounded-full overflow-hidden bg-[#2a2a2a] border border-white/10 flex items-center justify-center text-[10px] font-display font-bold flex-shrink-0">
      {src ? <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" /> : (name || "?")[0]?.toUpperCase()}
    </span>
  );
}

/**
 * Admin-only "credit to" picker: search ANY member (name, @username or email) and
 * pick who a scenepack is credited to. `value` is the chosen member object
 * ({id, name, username, avatar}); `me` is the admin themselves (what's used when
 * nothing else is picked). `onChange(member)` gets `me` back when "Me" is chosen.
 */
export default function CreditPicker({ me, value, onChange, testId = "credit-picker" }) {
  const current = value || me;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/admin/users-search?limit=12&q=${encodeURIComponent(q.trim())}`)
        .then((r) => { if (!cancelled) setResults(r.data); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, q ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, q]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("touchstart", away);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("touchstart", away); };
  }, [open]);

  const pick = (m) => { onChange(m); setOpen(false); setQ(""); };
  const isMe = current?.id === me?.id;

  return (
    <div ref={boxRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-button`}
        className="w-full sm:w-auto sm:min-w-[260px] flex items-center gap-2.5 rounded-lg border border-white/15 hover:border-white/30 bg-[#121212] px-3 py-2 text-left transition"
      >
        <Avatar name={current?.name} src={current?.avatar} />
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-display font-bold truncate">{isMe ? `Me (${current?.name || "admin"})` : current?.name || "Unknown"}</span>
          {!isMe && current?.username && <span className="block text-[10px] font-mono text-white/40 truncate">@{current.username}</span>}
        </span>
        <CaretDown size={12} className={`text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-2 rounded-xl border border-white/15 bg-[#161616] overflow-hidden" data-testid={`${testId}-panel`}>
          <div className="relative border-b border-white/10">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search any member — name, @username or email"
              data-testid={`${testId}-search`}
              className="!border-0 !rounded-none !bg-transparent !pl-9 !py-2.5 !text-base sm:!text-xs"
            />
          </div>
          <div className="max-h-56 overflow-y-auto overscroll-contain">
            {me && (
              <button type="button" onClick={() => pick(me)} data-testid={`${testId}-me`}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-white/5 text-left transition">
                <Avatar name={me.name} src={me.avatar} />
                <span className="text-xs font-display font-bold flex-1 truncate">Me ({me.name || "admin"})</span>
                {isMe && <Check size={12} className="text-[#FF8090]" />}
              </button>
            )}
            {loading && results.length === 0 && <div className="px-3 py-3 text-[11px] font-mono text-white/40">Searching…</div>}
            {!loading && results.length === 0 && <div className="px-3 py-3 text-[11px] font-mono text-white/40">No member found.</div>}
            {results.filter((m) => m.id !== me?.id).map((m) => (
              <button key={m.id} type="button" onClick={() => pick(m)} data-testid={`${testId}-option-${m.id}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-white/5 text-left transition">
                <Avatar name={m.name} src={m.avatar} />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-display font-bold truncate">{m.name || m.username}</span>
                  {m.username && <span className="block text-[10px] font-mono text-white/40 truncate">@{m.username}</span>}
                </span>
                {m.is_creator && <SealCheck size={12} weight="fill" className="text-[#FF8090] flex-shrink-0" title="Creator" />}
                {current?.id === m.id && <Check size={12} className="text-[#FF8090]" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
